import { useIsReadOnly } from '@hooks/useIsReadOnly'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { getPhotosSummary, getWorkOrderByReferenceNumber, getInvoiceByReferenceNumber } from '../../api/api'
import { PHOTO_SUMMARY_FIELD_MAP } from '../../variable-maps'
import { MultiPhotoSection, PhotoInput } from '@components/photo'
import { AdasBox, AdasButton, AdasContainer, AdasTypography } from '@components/wrapper-components'
import { PHOTO_LABELS, PARAM_NAMES, REDIRECT_URLS } from '../../constants'
import { Logger } from '../../logger'
import { FieldNames, OperationType, StatusEnum } from '../../enums/enums'
import { useScrollTop } from '../../utils/useScrollTop'

const SectionCard = ({ title, children }) => (
  <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
    <AdasBox className='mb-4 flex items-center justify-between'>
      <AdasTypography className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>{title}</AdasTypography>
    </AdasBox>
    {children}
  </AdasBox>
)
SectionCard.propTypes = { title: PropTypes.string, children: PropTypes.node }

export const PhotoPage = (props) => {
  useScrollTop()
  const { [PARAM_NAMES.REFERENCE_NUMBER]: referenceNumber } = useParams()
  const { fromInvoice = false } = props
  const isReadOnlyUser = useIsReadOnly()
  const history = useHistory()
  const [pageLoading, setPageLoading] = useState(true)
  const [dataUrls, setDataUrls] = useState({
    dashImageUrl: {},
    dashPostCalibrationImageUrl: {},
    weightImageUrl1: {},
    weightImageUrl2: {},
    driverFrontImageUrl: {},
    driverRearImageUrl: {},
    passengerFrontImageUrl: {},
    passengerRearImageUrl: {},
    driverFrontWheelImageUrl: {},
    driverRearWheelImageUrl: {},
    passengerFrontWheelImageUrl: {},
    passengerRearWheelImageUrl: {},
    alignmentImageUrl: {},
    alignmentImageUrl2: {}
  })

  const [additionalPhotos, setAdditionalPhotos] = useState([])
  const [frontCameraPhotos, setFrontCameraPhotos] = useState([])
  const [rearCameraPhotos, setRearCameraPhotos] = useState([])
  const [surroundViewPhotos, setSurroundViewPhotos] = useState([])
  const [blindSpotPhotos, setBlindSpotPhotos] = useState([])
  const [frontRadarCameraPhotos, setFrontRadarCameraPhotos] = useState([])
  const [labelList, setLabelList] = useState([])
  const [workOrderData, setWorkOrderData] = useState({})

  const redirectToForbiddenPage = () => history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)

  const isHistoricalImagesPresent = () =>
    frontCameraPhotos?.length > 0 ||
    blindSpotPhotos?.length > 0 ||
    surroundViewPhotos?.length > 0 ||
    rearCameraPhotos?.length > 0 ||
    frontRadarCameraPhotos?.length > 0

  const createDefaultLabelList = useCallback(() => {
    let list = []
    if (workOrderData.calibrations?.length > 0) list = list.concat(workOrderData.calibrations.map((i) => i.calibration.name))
    if (workOrderData.services?.length > 0) list = list.concat(workOrderData.services.map((i) => i.service.name))
    if (workOrderData.parts?.length > 0) list = list.concat(workOrderData.parts.map((i) => i.part.name))
    if (workOrderData.labours?.length > 0) list = list.concat(workOrderData.labours.map((i) => i.labour.name))
    if (workOrderData.customItems?.length > 0) list = list.concat(workOrderData.customItems.map((i) => i.name))
    setLabelList((prev) => Array.from(new Set([...prev, ...list])))
  }, [workOrderData.calibrations, workOrderData.services, workOrderData.parts, workOrderData.labours, workOrderData.customItems])

  const applyPhotoSummary = useCallback((photoSummaryData) => {
    if (!photoSummaryData) return
    setDataUrls((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(PHOTO_SUMMARY_FIELD_MAP)) {
        if (photoSummaryData[key]) next[PHOTO_SUMMARY_FIELD_MAP[key]] = photoSummaryData[key]
      }
      return next
    })
    setAdditionalPhotos(photoSummaryData.additionalPhotos || [])
    setFrontCameraPhotos(photoSummaryData.frontCameraPhotos || [])
    setBlindSpotPhotos(photoSummaryData.blindSpotMonitoringPhotos || [])
    setSurroundViewPhotos(photoSummaryData.surroundViewCameraPhotos || [])
    setRearCameraPhotos(photoSummaryData.rearCameraPhotos || [])
    setFrontRadarCameraPhotos(photoSummaryData.frontRadarCameraPhotos || [])
  }, [])

  const requestPhotoSummary = useCallback(
    async (params) => {
      try {
        const response = await getPhotosSummary(params)
        if (response.status === 200) applyPhotoSummary(response.data)
      } catch (error) {
        Logger.error({ payload: params, message: error })
      }
    },
    [applyPhotoSummary]
  )

  // Single bootstrap effect: fetch the WO/invoice, then the photos summary, all
  // gated behind one page-level loader so the user sees a single spinner.
  useEffect(() => {
    if (!referenceNumber) return undefined
    let cancelled = false

    const bootstrap = async () => {
      setPageLoading(true)
      try {
        let parent = null
        if (fromInvoice) {
          const response = await getInvoiceByReferenceNumber({ referenceNumber })
          if (cancelled) return
          if (response?.status !== 200 || !response.data?.invoiceDto) {
            redirectToForbiddenPage()
            return
          }
          parent = {
            safetySystemsItems: response.data.safetySystemsItems,
            calibrations: response.data.qwiCalibrations,
            services: response.data.qwiServices,
            parts: response.data.qwiParts,
            labours: response.data.qwiLabours,
            customItems: response.data.qwiCustomItems,
            id: response.data.workOrderDto.id,
            type: response.data.invoiceDto.type,
            status: response.data.invoiceDto.status
          }
        } else {
          const response = await getWorkOrderByReferenceNumber({ referenceNumber })
          if (cancelled) return
          if (response?.status !== 200) {
            redirectToForbiddenPage()
            return
          }
          if (StatusEnum[response.data?.workOrder?.status] === StatusEnum.MOVED) {
            redirectToForbiddenPage()
            return
          }
          parent = {
            safetySystemsItems: response.data.safetySystemsItems,
            calibrations: response.data.qwiCalibrations,
            services: response.data.qwiServices,
            parts: response.data.qwiParts,
            labours: response.data.qwiLabours,
            customItems: response.data.qwiCustomItems,
            id: response.data.workOrder.id,
            type: response.data.workOrder.type,
            status: response.data.workOrder.status
          }
        }
        if (cancelled || !parent?.id) return
        setWorkOrderData(parent)

        const photoRes = await getPhotosSummary({ workorderId: parent.id })
        if (cancelled) return
        if (photoRes?.status === 200) applyPhotoSummary(photoRes.data)
      } catch (error) {
        Logger.error({ message: error, payload: { referenceNumber, file: 'PhotoPage/bootstrap' } })
      } finally {
        if (!cancelled) setPageLoading(false)
      }
    }

    bootstrap()
    return () => {
      cancelled = true
    }
  }, [referenceNumber, fromInvoice, applyPhotoSummary])

  // Build the label list once we know the WO contents.
  useEffect(() => {
    if (workOrderData.id) createDefaultLabelList()
  }, [workOrderData.id, createDefaultLabelList])

  const refresh = useCallback(
    (refreshedData) => {
      if (refreshedData) {
        const multiplePhotosFields = [
          FieldNames.ADDITIONAL_PHOTOS,
          FieldNames.FRONT_CAMERA_PHOTOS,
          FieldNames.REAR_CAMERA_PHOTOS,
          FieldNames.SURROUND_VIEW_PHOTOS,
          FieldNames.BLIND_SPOT_PHOTOS
        ]
        const propertyName = refreshedData.imageLocation
        if (!multiplePhotosFields.includes(propertyName)) {
          if (refreshedData.operation === OperationType.ROTATE) {
            setDataUrls((prev) => ({
              ...prev,
              [propertyName]: { ...prev[propertyName], img_rotation: refreshedData.img_rotation }
            }))
          }
          if (refreshedData.operation === OperationType.DELETE) {
            setDataUrls((prev) => ({ ...prev, [propertyName]: null }))
          }
          if (refreshedData.operation === OperationType.RETAKE) {
            setDataUrls((prev) => ({
              ...prev,
              [propertyName]: { img_rotation: refreshedData.img_rotation, id: refreshedData.attachmentId }
            }))
          }
        }
      } else if (workOrderData.id) {
        requestPhotoSummary({ workorderId: workOrderData.id })
      }
    },
    [workOrderData.id, requestPhotoSummary]
  )

  const generatePhotoInputProps = (fieldLocation, labelText, title) => {
    const fieldData = dataUrls[fieldLocation] || {}
    return {
      labelText,
      imageLocation: fieldLocation,
      workorderId: workOrderData.id,
      onUpdate: refresh,
      disabled: disableEdit,
      properties: fieldData,
      imageId: fieldData.id || '',
      img_rotation: fieldData.img_rotation || 0,
      title
    }
  }

  const isServiceOnly = workOrderData.type === 'SERVICE_ONLY'
  const disableEdit = StatusEnum[workOrderData.status] === StatusEnum.CLOSED || fromInvoice || isReadOnlyUser

  if (pageLoading) {
    return (
      <AdasContainer maxWidth={false}>
        <AdasBox className='flex min-h-[60vh] flex-col items-center justify-center gap-3'>
          <Loader2 className='h-7 w-7 animate-spin text-[#1F6FEB]' />
          <AdasTypography className='text-[14px] text-[#475569]'>Loading photos...</AdasTypography>
        </AdasBox>
      </AdasContainer>
    )
  }

  return (
    <AdasContainer maxWidth={false}>
      <AdasBox className='mb-[60px] mt-5'>
        {/* Header — Back link + title (desktop only) */}
        <AdasBox className='mb-4'>
          <AdasBox className='hidden min-[435px]:block'>
            <button
              type='button'
              onClick={() => history.goBack()}
              className='mb-1 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1F6FEB] hover:underline'
            >
              Back to {fromInvoice ? 'Invoice Summary' : 'WO Summary'}
            </button>
            <AdasTypography className='text-[28px] font-bold leading-tight text-[#0A1628]'>Photos</AdasTypography>
          </AdasBox>
        </AdasBox>

        <SectionCard title='Dashboard'>
          <AdasBox className='mx-auto grid grid-cols-2 gap-3 sm:gap-[64px]'>
            <PhotoInput {...generatePhotoInputProps(FieldNames.DASH_IMAGE_URL, 'DASH PRE CALIBRATION')} />
            <PhotoInput {...generatePhotoInputProps('dashPostCalibrationImageUrl', 'DASH POST CALIBRATION')} />
          </AdasBox>
        </SectionCard>

        {!isServiceOnly && (
          <SectionCard title='Fuel Weight Compensation'>
            <AdasBox className='mx-auto grid grid-cols-2 gap-3 sm:gap-[64px]'>
              <PhotoInput {...generatePhotoInputProps(FieldNames.WEIGHT_IMAGE_URL_1, 'ADD PHOTO OF WEIGHT')} />
              <PhotoInput {...generatePhotoInputProps(FieldNames.WEIGHT_IMAGE_URL_2, 'ADD PHOTO OF WEIGHT')} />
            </AdasBox>
          </SectionCard>
        )}

        <SectionCard title='Four Corners'>
          <AdasBox className='mx-auto grid grid-cols-2 gap-3 sm:gap-[64px]'>
            <PhotoInput {...generatePhotoInputProps(FieldNames.DRIVER_FRONT_IMAGE_URL, 'DRIVER SIDE FRONT CORNER')} />
            <PhotoInput {...generatePhotoInputProps(FieldNames.PASSENGER_FRONT_IMAGE_URL, 'PASSENGER SIDE FRONT CORNER')} />
            <PhotoInput {...generatePhotoInputProps(FieldNames.DRIVER_REAR_IMAGE_URL, 'DRIVER SIDE REAR CORNER')} />
            <PhotoInput {...generatePhotoInputProps(FieldNames.PASSENGER_REAR_IMAGE_URL, 'PASSENGER SIDE REAR CORNER')} />
          </AdasBox>
        </SectionCard>

        <SectionCard title='Tire Pressure'>
          <AdasBox className='mx-auto grid grid-cols-2 gap-3 sm:gap-[64px]'>
            <PhotoInput {...generatePhotoInputProps(FieldNames.DRIVER_FRONT_WHEEL_IMAGE_URL, 'DRIVER SIDE FRONT WHEEL')} />
            <PhotoInput {...generatePhotoInputProps(FieldNames.PASSENGER_FRONT_WHEEL_IMAGE_URL, 'PASSENGER SIDE FRONT WHEEL')} />
            <PhotoInput {...generatePhotoInputProps(FieldNames.DRIVER_REAR_WHEEL_IMAGE_URL, 'DRIVER SIDE REAR WHEEL')} />
            <PhotoInput {...generatePhotoInputProps(FieldNames.PASSENGER_REAR_WHEEL_IMAGE_URL, 'PASSENGER SIDE REAR WHEEL')} />
          </AdasBox>
        </SectionCard>

        <SectionCard title='Battery'>
          <AdasBox className='mx-auto grid grid-cols-2 gap-3 sm:gap-[64px]'>
            <AdasBox className='col-span-2 w-[calc(50%-6px)] sm:w-[calc(50%-32px)] justify-self-center'>
              <PhotoInput {...generatePhotoInputProps(FieldNames.BATTERY_HEALTH_IMAGE_URL, 'ADD PHOTO OF BATTERY HEALTH CHECK')} />
            </AdasBox>
          </AdasBox>
        </SectionCard>

        <SectionCard title='Wheel Alignment'>
          {(() => {
            const showSecond = dataUrls[FieldNames.ALIGNMENT_IMAGE_URL]?.id || dataUrls[FieldNames.ALIGNMENT_IMAGE_URL_2]?.id
            return (
              <AdasBox className='mx-auto grid grid-cols-2 gap-3 sm:gap-[64px]'>
                <AdasBox className={showSecond ? 'flex' : 'col-span-2 w-[calc(50%-6px)] sm:w-[calc(50%-32px)] justify-self-center'}>
                  <PhotoInput
                    {...generatePhotoInputProps(FieldNames.ALIGNMENT_IMAGE_URL, 'ADD PHOTO OF WHEEL ALIGNMENT')}
                    allowDelete={true}
                  />
                </AdasBox>
                {showSecond && (
                  <PhotoInput
                    {...generatePhotoInputProps(FieldNames.ALIGNMENT_IMAGE_URL_2, 'ADD PHOTO OF WHEEL ALIGNMENT')}
                    allowDelete={true}
                  />
                )}
              </AdasBox>
            )
          })()}
        </SectionCard>

        {(!isServiceOnly || isHistoricalImagesPresent()) && (
          <>
            <SectionCard title={PHOTO_LABELS.FRONT_CAMERA}>
              <MultiPhotoSection
                title={PHOTO_LABELS.FRONT_CAMERA}
                addText={PHOTO_LABELS.ADD_PHOTO}
                photoInfoArray={frontCameraPhotos}
                workOrderId={workOrderData.id}
                refresh={refresh}
                disabled={disableEdit}
                imageLocation={FieldNames.FRONT_CAMERA_PHOTOS}
                labelList={labelList}
                setMultiPhotos={setFrontCameraPhotos}
                isMultiUpload={true}
                maxPhotos={16}
              />
            </SectionCard>
            <SectionCard title={PHOTO_LABELS.REAR_CAMERA}>
              <MultiPhotoSection
                title={PHOTO_LABELS.REAR_CAMERA}
                addText={PHOTO_LABELS.ADD_PHOTO}
                photoInfoArray={rearCameraPhotos}
                workOrderId={workOrderData.id}
                refresh={refresh}
                disabled={disableEdit}
                imageLocation={FieldNames.REAR_CAMERA_PHOTOS}
                labelList={labelList}
                setMultiPhotos={setRearCameraPhotos}
                isMultiUpload={true}
                maxPhotos={16}
              />
            </SectionCard>
            <SectionCard title={PHOTO_LABELS.SURROUND_VIEW_CAMERA}>
              <MultiPhotoSection
                title={PHOTO_LABELS.SURROUND_VIEW_CAMERA}
                addText={PHOTO_LABELS.ADD_PHOTO}
                photoInfoArray={surroundViewPhotos}
                workOrderId={workOrderData.id}
                refresh={refresh}
                disabled={disableEdit}
                imageLocation={FieldNames.SURROUND_VIEW_PHOTOS}
                labelList={labelList}
                isMultiUpload={true}
                maxPhotos={24}
              />
            </SectionCard>
            <SectionCard title={PHOTO_LABELS.BLIND_SPOT_MONITORING}>
              <MultiPhotoSection
                title={PHOTO_LABELS.BLIND_SPOT_MONITORING}
                addText={PHOTO_LABELS.ADD_PHOTO}
                photoInfoArray={blindSpotPhotos}
                workOrderId={workOrderData.id}
                refresh={refresh}
                disabled={disableEdit}
                imageLocation={FieldNames.BLIND_SPOT_PHOTOS}
                labelList={labelList}
                isMultiUpload={true}
                maxPhotos={16}
              />
            </SectionCard>
            <SectionCard title={PHOTO_LABELS.FRONT_RADAR}>
              <MultiPhotoSection
                title={PHOTO_LABELS.FRONT_RADAR}
                addText={PHOTO_LABELS.ADD_PHOTO}
                photoInfoArray={frontRadarCameraPhotos}
                workOrderId={workOrderData.id}
                refresh={refresh}
                disabled={disableEdit}
                imageLocation={FieldNames.FRONT_RADAR_CAMERA_PHOTOS}
                labelList={labelList}
                isMultiUpload={true}
                maxPhotos={16}
              />
            </SectionCard>
          </>
        )}

        <SectionCard title={PHOTO_LABELS.ADDITIONAL_PHOTOS}>
          <MultiPhotoSection
            title={PHOTO_LABELS.ADDITIONAL_PHOTOS}
            photoInfoArray={additionalPhotos}
            workOrderId={workOrderData.id}
            refresh={refresh}
            disabled={disableEdit}
            imageLocation={FieldNames.ADDITIONAL_PHOTOS}
            labelList={labelList}
            isMultiUpload={true}
            maxPhotos={24}
          />
        </SectionCard>

        {/* Footer — DONE (filled navy) + Cancel (outlined) */}
        <AdasBox className='flex items-center gap-3'>
          <AdasButton
            variant='contained'
            onClick={() => history.goBack()}
            className='rounded-lg bg-[#0A1628] px-6 font-semibold text-white hover:bg-[#1F2D3D]'
          >
            DONE
          </AdasButton>
          <AdasButton variant='outlined' onClick={() => history.goBack()} className='rounded-lg px-6 font-semibold'>
            Cancel
          </AdasButton>
        </AdasBox>
      </AdasBox>
    </AdasContainer>
  )
}

PhotoPage.propTypes = {
  fromInvoice: PropTypes.bool
}
